import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mjglbqwz";

export default function Contact() {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });

  useEffect(() => {
    const nodes = ref.current?.querySelectorAll(".reveal") || [];
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add("in"));
    }, { threshold: 0.15 });
    nodes.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, []);

  const onChange = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (status.message) setStatus({ type: "", message: "" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ type: "error", message: "Please fill in name, email, and message." });
      return;
    }

    try {
      setSubmitting(true);
      setStatus({ type: "", message: "" });

      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim() || "",
          message: form.message.trim(),
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        const errorMessage = payload?.errors?.[0]?.message || payload?.error || "Something went wrong. Please try again.";
        throw new Error(errorMessage);
      }

      setStatus({ type: "success", message: "Message received." });
    } catch (error) {
      setStatus({ type: "error", message: error instanceof Error ? error.message : "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: "transparent",
    border: "none",
    borderBottom: "1px solid var(--rule)",
    borderRadius: 0,
    height: 44,
    padding: "0 2px",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    color: "var(--ink)",
  };

  return (
    <section ref={ref} id="contact" data-testid="contact-section" style={{ padding: "140px 0 120px", position: "relative" }}>
      <div className="mx-auto" style={{ maxWidth: 1100, padding: "0 40px", textAlign: "center" }}>
        <h2
          data-testid="contact-heading"
          className="font-serif reveal delay-1"
          style={{ margin: 0, fontSize: "clamp(44px, 6vw, 92px)", lineHeight: 1.02, letterSpacing: "-0.02em" }}
        >
          Got a campaign that could<br /><em style={{ color: "var(--ink-muted)" }}>travel further?</em>
        </h2>

        <p
          className="reveal delay-2"
          style={{
            marginTop: 28,
            fontSize: 18,
            lineHeight: 1.55,
            color: "var(--ink-muted)",
            maxWidth: 520,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          Start with a pilot. €1,200 for 10 cutdowns. No retainer.
          <br />
          We take on three clients at a time.
        </p>

        <div className="reveal delay-3" style={{ marginTop: 48 }}>
          <Dialog
            open={open}
            onOpenChange={(isOpen) => {
              setOpen(isOpen);
              setStatus({ type: "", message: "" });
              setForm({ name: "", email: "", company: "", message: "" });
            }}
          >
            <DialogTrigger asChild>
              <button
                data-testid="contact-open-btn"
                style={{
                  background: "var(--ink)",
                  color: "var(--bone)",
                  border: "none",
                  padding: "18px 36px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  cursor: "pointer",
                  borderRadius: 2,
                }}
              >
                BOOK A PILOT →
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid="contact-dialog"
              style={{
                background: "var(--bone)",
                color: "var(--ink)",
                border: "1px solid var(--rule)",
                borderRadius: 2,
                maxWidth: 560,
              }}
            >
              {status.type === "success" ? (
                <div style={{ display: "grid", gap: 24, paddingTop: 6 }}>
                  <h3 className="font-serif" style={{ margin: 0, fontSize: 46, lineHeight: 1.02, letterSpacing: "-0.02em", fontWeight: 400 }}>
                    Message received.
                  </h3>
                  <p style={{ margin: 0, color: "var(--ink-muted)", fontSize: 16, lineHeight: 1.5 }}>
                    We’ll read it and get back to you within 12 hours.
                  </p>
                  <p className="font-mono" style={{ margin: 0, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
                    BARCELONA · EUROPE
                  </p>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      style={{
                        background: "var(--ink)",
                        color: "var(--bone)",
                        border: "none",
                        padding: "14px 28px",
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 13,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        cursor: "pointer",
                        borderRadius: 2,
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-serif" style={{ fontSize: 36, lineHeight: 1.05, letterSpacing: "-0.02em", fontWeight: 400 }}>
                      Book a pilot.
                    </DialogTitle>
                    <DialogDescription style={{ color: "var(--ink-muted)", fontSize: 14, marginTop: 6 }}>
                      We reply within 12 hours.
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={onSubmit} style={{ marginTop: 18, display: "grid", gap: 20 }}>
                <div>
                  <Label htmlFor="name" className="font-mono" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Name</Label>
                  <Input id="name" data-testid="contact-name" value={form.name} onChange={onChange("name")} style={inputStyle} placeholder="Your name" />
                </div>
                <div>
                  <Label htmlFor="email" className="font-mono" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Email</Label>
                  <Input id="email" data-testid="contact-email" type="email" value={form.email} onChange={onChange("email")} style={inputStyle} placeholder="you@brand.com" />
                </div>
                <div>
                  <Label htmlFor="company" className="font-mono" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>Company (optional)</Label>
                  <Input id="company" data-testid="contact-company" value={form.company} onChange={onChange("company")} style={inputStyle} placeholder="Brand or studio" />
                </div>
                <div>
                  <Label htmlFor="message" className="font-mono" style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>The footage</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={onChange("message")}
                    rows={4}
                    placeholder="What did you shoot? What's it for?"
                    style={{
                      background: "transparent",
                      border: "1px solid var(--rule)",
                      borderRadius: 2,
                      padding: 12,
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 15,
                      color: "var(--ink)",
                      resize: "vertical",
                    }}
                  />
                </div>

                {status.message ? (
                  <p
                    role="status"
                    style={{
                      margin: 0,
                      fontSize: 13,
                      lineHeight: 1.4,
                      color: status.type === "error" ? "#a23a3a" : "var(--ink-muted)",
                      textAlign: "left",
                    }}
                  >
                    {status.message}
                  </p>
                ) : null}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <span className="font-mono" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
                    Barcelona · Europe
                  </span>
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    style={{
                      background: "var(--ink)",
                      color: "var(--bone)",
                      border: "none",
                      padding: "14px 28px",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 13,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      cursor: submitting ? "wait" : "pointer",
                      opacity: submitting ? 0.7 : 1,
                      borderRadius: 2,
                    }}
                  >
                    {submitting ? "Booking…" : "BOOK A PILOT →"}
                  </button>
                </div>
              </form>
                </>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <p className="reveal delay-4 font-mono" style={{ marginTop: 32, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--ink-muted)" }}>
          Based in Barcelona · Working across Europe
        </p>
      </div>
    </section>
  );
}
